import { css } from "@emotion/react";
import { useEffect, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Button } from "./button";
import { useTranslation } from "react-i18next";
import { modalActions, modalDescription } from "@/styles/shared";

const overlayStyle = css`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 24px;

  @media (max-width: 640px) {
    padding: 12px;
    align-items: flex-start;
    padding-top: 60px;
  }
`;

const modalStyle = css`
  background: var(--container-bg);
  border-radius: var(--radius-xl);
  box-shadow: 0 8px 32px var(--shadow-dark);
  max-width: 500px;
  width: 100%;
  max-height: calc(100vh - 48px);
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  overflow-y: auto;

  @media (max-width: 640px) {
    padding: 16px;
    gap: 16px;
    border-radius: var(--radius-lg);
    max-height: calc(100vh - 72px);
  }
`;

const modalTitleStyle = css`
  font-family: var(--font-heading);
  font-size: var(--text-xl);
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
  transition: color 0.3s ease;

  @media (max-width: 640px) {
    font-size: var(--text-lg);
  }
`;

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  /** Extra CSS class on the modal container */
  className?: string;
}

export function Modal({ open, onClose, title, children, className }: ModalProps) {
  useEffect(() => {
    if (!open) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };

    // Lock body scroll when modal is open
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = originalOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div
      css={overlayStyle}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        css={modalStyle}
        className={className}
        onClick={(e) => e.stopPropagation()}
      >
        <h3 css={modalTitleStyle}>{title}</h3>
        {children}
      </div>
    </div>,
    document.body,
  );
}

interface ConfirmModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  loading?: boolean;
  variant?: "primary" | "danger";
}

export function ConfirmModal({
  open,
  onClose,
  onConfirm,
  title,
  message,
  confirmLabel,
  cancelLabel,
  loading = false,
  variant = "primary",
}: ConfirmModalProps) {
  const { t } = useTranslation();

  return (
    <Modal open={open} onClose={loading ? () => {} : onClose} title={title}>
      <p css={modalDescription}>{message}</p>
      <div css={modalActions}>
        <Button variant="secondary" onClick={onClose} disabled={loading}>
          {cancelLabel ?? t("common.cancel")}
        </Button>
        <Button variant={variant} onClick={onConfirm} disabled={loading}>
          {confirmLabel ?? t("common.confirm")}
        </Button>
      </div>
    </Modal>
  );
}
